import { Client } from "@/features/clients/types/clients";
import api from "@/lib/axios";

export enum ClientsKeys {
  Clients = "clients",
  Client = "client",
  CreateReferredClient = "create-referred-client",
  ReferredClients = "referred-clients",
}

// Get all customers for the logged-in licensee
export const getLicenseeClients = async (): Promise<Client[]> => {
  const response = await api.get("/customers");
  return response.data;
};

// Create a new customer for the connected account
export const createClient = async (
  customer: Partial<Client>,
): Promise<Client> => {
  const response = await api.post("/customers", customer);
  return response.data;
};

// Update an existing customer
export const updateClient = async (
  customerId: string,
  customer: Partial<Client>,
): Promise<Client> => {
  const response = await api.patch(`/customers/${customerId}`, customer);
  return response.data;
};

// Interface for creating referred customers
export interface CreateReferredClientDto {
  licenseeId: string;
  name: string;
  customerEmail: string;
  notes?: string;
}

// Interface for referred customer response
export interface ReferredClient {
  _id: string;
  mentorId?: string;
  adminId?: string; // Admin/Stack who referred the client
  licenseeId: {
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  name?: string;
  customerEmail: string;
  customerStripeId?: string;
  notes?: string;
  status: "referred" | "paid";
  totalFeesCollected: number;
  createdAt: string;
  updatedAt: string;
}

// Create a referred customer (for mentors)
export const createReferredClient = async (
  data: CreateReferredClientDto,
): Promise<ReferredClient> => {
  const response = await api.post("/customers/referred", data);
  return response.data;
};

// Get referred clients for the logged-in mentor
export const getReferredClients = async (
  licenseeId?: string,
): Promise<ReferredClient[]> => {
  const params = licenseeId ? { licenseeId } : {};
  const response = await api.get("/customers/referred", { params });
  return response.data;
};

// Create a referred customer (for Stack/admins)
export const createReferredClientByStack = async (
  data: CreateReferredClientDto,
): Promise<ReferredClient> => {
  const response = await api.post("/customers/referred/stack", data);
  return response.data;
};

// Get referred clients for the logged-in Stack/admin user
export const getReferredClientsByStack = async (
  licenseeId?: string,
): Promise<ReferredClient[]> => {
  const params = licenseeId ? { licenseeId } : {};
  const response = await api.get("/customers/referred/stack", { params });
  return response.data;
};

// Get all referred clients by all Stack/admin users (for admins only)
export const getAllReferredClientsByStack = async (): Promise<
  ReferredClient[]
> => {
  const response = await api.get("/customers/referred/stack/all");
  return response.data;
};
